import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { WorkflowService } from '../../../../../../../../shared/services/workflow.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Account } from '../../../../../../../../shared/models';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../state/app.state';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsService } from '../../../../../../core/projects.service';
import { ProjectDetailsService } from '../../project-details.service';
import { ExpressionEvaluatorService } from '../../../../../../../../shared/services/expression-evaluator.service';
import { AuthService } from '../../../../../../../../shared/services';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import {
  ClearChangeOrdersRequestWithId,
  FetchAllBidItemsRequest,
  FetchBasisOfPayOptionsRequest,
  FetchChangeInWorkCodeOptionsRequest,
  FetchChangeOrdersRequestWithIdRequest,
  FetchAvailableCorsRequest,
  SaveChangeOrdersRequestRequest,
  UnlinkRequestFromChangeOrderRequest,
} from '../../../../../../core/projects.actions';
import { availableCors, changeInWorkCodeOptions, changeOrdersRequest, projectDetails } from '../../../../../../core/projects.selectors';
import { GetVideoIdRequest, UploadFileRequest } from '../../../../../../../../shared/core/shared.actions';
import { classifyFile } from 'src/app/shared/helpers';
import { ChangeOrdersRequestReportComponent } from '../change-orders-request-report/change-orders-request-report.component';
import { CreateChangeOrderDialogComponent } from '../create-change-order-dialog/create-change-order-dialog.component';
import { AnalyticsService } from '../../../../../../../../shared/services/analytics.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components';
import { shortRequestNo } from '../../change-in-work-request/change-in-work-request-line-item/unit-price-rules';

// change_order.comments is varchar(5000) in Postgres, so this is a real backend
// limit rather than a UI preference. It is enforced as an explicit validator
// because the shared text-area only sets attr.maxlength, which the DOM ignores
// for programmatic setValue — and drops the cap on an already-long value.
const CHANGE_ORDER_COMMENTS_MAX = 5000;

@Component({
    selector: 'app-change-orders-request-form',
    templateUrl: './change-orders-request-form.component.html',
    styleUrls: ['./change-orders-request-form.component.scss'],
    standalone: false
})
export class ChangeOrdersRequestFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInputRef: any;
  @ViewChild('displayErrorRef') displayErrorElement: ElementRef;
  /** Shared with the change request form's price rules — exposed for the template. */
  readonly shortRequestNo = shortRequestNo;
  data: any;
  lineErrors: Record<string, boolean> = {};
  limits: any;
  bidItemsList = [];
  filteredBidItemsList = [];
  focusedIn = false;
  searchText: string;
  errorMessages: any = {};
  errorMessageMap = {
    descriptionOfChange: {
      required: 'This field is required.',
    },
    requestNo: {
      required: 'This field is required.',
    },
    changeOrderNo: {
      required: 'This field is required.',
    },
    subContractor: {
      required: 'This field is required.',
    },
    pictures: {
      required: 'This field is required.',
    },
    videos: {
      required: 'This field is required.',
    },
  };
  form = new FormGroup<any>({
    requestNo: new FormControl({ value: null, disabled: false }),
    changeOrderNo: new FormControl({ value: null, disabled: false }),
    subContractor: new FormControl({ value: null, disabled: true }),
    descriptionOfChange: new FormControl({ value: null, disabled: false }, [
      Validators.maxLength(CHANGE_ORDER_COMMENTS_MAX),
    ]),
    location: new FormControl({ value: null, disabled: false }),
    pictures: new FormControl({ value: '[]', disabled: false }),
    videos: new FormControl({ value: '[]', disabled: false }),
  });
  lineKeys: any[] = [];
  selectedLineId: any;
  totalAmount = 0;
  totalNewWorkingDays = 0;
  basisOfPayOptions = [];
  changeInWorkCodeOptions = [];
  project: any;
  subContractorOptions = [];
  mediaFiles = [];
  videoToBeDeletedFromMuxDb = [];
  isFormDirty = false;
  editable = true;
  addMenuOptions = [
    {
      key: 'picture',
      icon: 'paperclip',
      label: 'Attachments',
      handler: () => {
        this.fileInputRef.nativeElement.click();
      },
    },
  ];
  actions = [];
  creator = false;
  account: Account;
  displayError = false;
  displayLineError = false;
  displayTotalNewWorkingDaysError = false;
  displayTotalAmountError = false;
  totalAmountArray = [];
  totalAdditionalWorkingDaysArray = [];
  linkedRequests: any[] = [];
  showAllLinkedRequests = false;
  lineSources: Record<string, { request_no: string; uuid: string } | null> = {};
  corTotalAmount = 0;
  descriptionMaxLength = CHANGE_ORDER_COMMENTS_MAX;

  get descriptionLengthError(): string {
    const errors = this.form.get('descriptionOfChange')?.errors;
    return errors?.['maxlength']
      ? `description exceeds ${errors['maxlength'].requiredLength} characters — please shorten it`
      : '';
  }
  corTotalDays = 0;
  changeOrderCreateMode: string = 'manual';
  availableCorCount = 0;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private workflowService: WorkflowService,
    private commonService: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
    private projectDetailsService: ProjectDetailsService,
    private cdRef: ChangeDetectorRef,
    private evaluator: ExpressionEvaluatorService,
    public authService: AuthService,
    public dialog: MatDialog,
    private analyticsService: AnalyticsService,
  ) {

    this.route.queryParams
      .pipe(
        map(qp => qp?.cor || null),
        distinctUntilChanged(),
        takeUntil(this.onDestroy),
      )
      .subscribe(cor => {
        if (cor) {
          this.store.dispatch(FetchChangeOrdersRequestWithIdRequest({ payload: { changeOrdersRequestId: cor } }));
        } else {
          this.store.dispatch(ClearChangeOrdersRequestWithId());
        }
      });
  }

  ngOnInit() {
    this.account = this.authService.getCurrentUser();

    this.projectsService.totalAmount
      .pipe(debounceTime(200), takeUntil(this.onDestroy))
      .subscribe(() => {
        let total = 0;
        const raw = this.form.getRawValue();

        Object.keys(raw).forEach((key) => {
          if (key.startsWith('line_')) {
            const lineValue = raw[key];
            if (lineValue && typeof lineValue.totalAmount === 'number') {
              total += lineValue.totalAmount;
            }
          }
        });

        this.totalAmount = total;
      });

    this.projectsService.totalAdditionalWorkingDays
      .pipe(debounceTime(200), takeUntil(this.onDestroy))
      .subscribe(() => {
        let twd = 0;
        const raw = this.form.getRawValue();

        Object.keys(raw).forEach((key) => {
          if (key.startsWith('line_')) {
            const lineValue = raw[key];
            if (lineValue) {
              twd += +lineValue.additionalWorkingDays;
            }
          }
        });

        this.totalNewWorkingDays = twd;
      });

    this.projectsService.totalAdditionalWorkingDays
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.totalAdditionalWorkingDaysArray.push(Number(data));
        this.totalNewWorkingDays = this.totalAdditionalWorkingDaysArray.reduce((sum, val) => sum + val, 0);
      });

    this.projectsService.isAnyFormDirty
      .pipe(takeUntil(this.onDestroy))
      .subscribe((dirty) => {
        this.isFormDirty = dirty;
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.project = project;
        if (project && Object.keys(project).length) {
          this.changeOrderCreateMode = project?.source_entity_extra_attributes?.change_order_create_mode || 'manual';
        }
        const lookupPayload = {
          entityId: project?.owner_organization?.id,
          entityType: 'organization',
        };

        this.subContractorOptions = project.sub_contractors
          .filter(o => o.prime)
          .map(o => {
            return {
              ...o,
              value: o.id,
              label: o.name,
            };
          });

        setTimeout(() => {
          this.form.patchValue({
            subContractor: this.subContractorOptions?.[0]?.value,
            delegatedLimit: project?.['change_order_deligated_limit'],
          }, { emitEvent: false });
        });

        this.store.dispatch(FetchAllBidItemsRequest({
          payload: {
            projectId: project?.id,
            qp: {
              include: [
                {
                  bid_items: ['fields'],
                },
              ],
            },
          },
        }));

        this.store.dispatch(FetchBasisOfPayOptionsRequest({
          payload: lookupPayload,
        }));

        this.store.dispatch(FetchChangeInWorkCodeOptionsRequest({
          payload: lookupPayload,
        }));

        // Fetch available CORs count for "Add request" link
        if (this.changeOrderCreateMode !== 'auto') {
          this.store.dispatch(FetchAvailableCorsRequest({
            payload: { projectId: project?.id },
          }));
        }
      });

    this.store.select(availableCors)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.availableCorCount = data?.list?.length || 0;
      });

    this.store.select(changeInWorkCodeOptions)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.changeInWorkCodeOptions = data;
      });

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];

        items.forEach((eachItem: any) => {
          if (eachItem?.['fileType'] === 'picture' || eachItem?.['fileType'] === 'document') {
            let pictures: any = this.form.get('pictures')?.value || '[]';
            pictures = JSON.parse(pictures);

            pictures.push(eachItem);
            this.form?.get('pictures')?.setValue(JSON.stringify(pictures), { emitEvent: false });
            this.mediaFiles.push(eachItem);
          } else if (eachItem?.['fileType'] === 'video') {
            let videos: any = this.form.get('videos')?.value || '[]';
            videos = JSON.parse(videos);

            videos.push(eachItem);
            this.form
              ?.get('videos')
              ?.setValue(JSON.stringify(videos), { emitEvent: false });
            this.mediaFiles.push(eachItem);
          }
        });
        this.projectsService.isAnyFormDirty.next(true);
      });

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.projectsService.isAnyFormDirty.next(true);
      });

    this.projectsService.addValueToParentForm
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.form.controls?.[data?.lineKey]?.patchValue({
          ...data.value,
        }, { emitEvent: false }); // ⚠ prevents loop
      });

    this.projectDetailsService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.save();
      });

    this.store.select(changeOrdersRequest)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.data = {};
        this.lineKeys = [];
        this.mediaFiles = [];
        this.totalAmountArray = [];
        this.totalAdditionalWorkingDaysArray = [];

        this.data = data;

        this.lineSources = {};
        // Editable only for a new CO or a draft/rejected one; submitted/approved are read-only
        this.editable = !this.data?.id || this.data?.status === 'draft' || this.data?.status === 'rejected';

        Object.keys(this.form.controls)
          .filter(key => key.startsWith('line_'))
          .forEach(key => this.form.removeControl(key, { emitEvent: false }));

        if (this.data?.id) {
          this.form.patchValue({
            requestNo: this.data?.request_no,
            changeOrderNo: this.data?.order_number,
            subContractor: this.data?.sub_contractor?.id,
            descriptionOfChange: this.data?.comments,
            location: this.data?.location,
            pictures: JSON.stringify(this.data?.pictures),
            videos: JSON.stringify(this.data?.videos),
          }, { emitEvent: false });

          this.mediaFiles.push(...(this.data?.media || []));

          this.data?.lines?.forEach((o, i) => {
            this.totalAdditionalWorkingDaysArray.push(Number(o?.additional_working_days) || 0);

            let result = 0;
            if (o?.original_unit_price != null) {
              // Repricing snapshot: new_price * (orig + delta) - orig_price * orig
              const newPrice = Number(o?.unit_price) || 0;
              const deltaQty = Number(o?.quantity) || 0;
              const origQty = Number(o?.original_quantity) || 0;
              const origPrice = Number(o?.original_unit_price) || 0;
              result = newPrice * (origQty + deltaQty) - origPrice * origQty;
            } else if (o?.unit_price && o?.quantity) {
              result = (Number(o.unit_price) || 0) * (Number(o.quantity) || 0);
            }
            this.totalAmountArray.push(result);

            const lineKey = `line_${i + 1}`;

            const newItemGroup = new FormGroup({
              searchText: new FormControl({ value: '', disabled: false }),
              itemNo: new FormControl({ value: o?.item, disabled: true }, { validators: [Validators.required] }),
              id: new FormControl({ value: o?.id, disabled: false }),
              item: new FormControl({ value: `${o?.item} ${o?.description}`, disabled: false }),
              uom: new FormControl({ value: o?.uom, disabled: false }, { validators: [Validators.required] }),
              desc: new FormControl({ value: o?.description, disabled: false }, { validators: [Validators.required] }),
              basisOfPay: new FormControl({ value: o?.basis_of_pay, disabled: false }, { validators: [Validators.required] }),
              quantity: new FormControl({ value: o?.original_quantity ?? null, disabled: false }),
              originalUnitPrice: new FormControl({ value: o?.original_unit_price ?? null, disabled: false }),
              changeInQuantity: new FormControl({ value: o?.quantity, disabled: false }, { validators: [Validators.required] }),
              unitPrice: new FormControl({ value: o?.unit_price, disabled: false }, { validators: [Validators.required] }),
              totalAmount: new FormControl({ value: result, disabled: false }, { validators: [Validators.required] }),
              additionalWorkingDays: new FormControl({
                value: o?.additional_working_days,
                disabled: false,
              }, { validators: [Validators.required] }),
            });

            this.form.addControl(lineKey, newItemGroup, { emitEvent: false });

            this.lineSources[lineKey] = o?.source_request_no
              ? { request_no: o.source_request_no, uuid: o.source_request_uuid }
              : null;
            if (this.lineSources[lineKey] || !this.editable) {
              newItemGroup.disable({ emitEvent: false });
            }
          });

          this.lineKeys = this.getLineKeys() || [];

          this.totalAmount = this.totalAmountArray.reduce((sum, val) => sum + val, 0);
          // Use CO header working days (set from linked COR days), fall back to line sum
          const headerDays = parseInt(this.data?.additional_working_days, 10) || 0;
          const lineDays = this.totalAdditionalWorkingDaysArray.reduce((sum, val) => sum + val, 0);
          this.totalNewWorkingDays = headerDays || lineDays;

          this.actions = this.workflowService.canExecute() ? (this.data?.['next-actions']?.[0]?.actions || []) : [];

          if (this.actions?.length && this.data.createdBy && Object.keys(this.data.createdBy).length > 0) {
            const crtBy = `${this.data.createdBy.first_name} ${this.data.createdBy.last_name}`;
            this.creator = crtBy === this.account.name;
          }

          this.projectsService.fetchLinkedRequests({ changeOrderId: this.data.id })
            .pipe(takeUntil(this.onDestroy))
            .subscribe(response => {
              this.linkedRequests = response?.data || [];
            });
        }
      });
  }

  ngAfterViewInit() {
    this.projectsService.isAnyFormDirty.next(false);
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  getLineKeys() {
    this.lineKeys = [];
    return Object.keys(this.form.controls || {})
      .filter(key => key.startsWith('line_'));
  }

  selectLine(key: any) {
    this.searchText = null;
    this.filteredBidItemsList = this.bidItemsList;
    this.selectedLineId = this.selectedLineId === key ? null : key;
    this.projectsService.isAnyFormDirty.next(false);
  }

  deleteLine(key: any) {
    this.form.removeControl(key, { emitEvent: true });
    this.lineKeys = this.lineKeys.filter(o => o !== key);
    this.selectedLineId = null;

    this.totalAmountArray = [];
    this.totalAdditionalWorkingDaysArray = [];

    const formValues = this.form.getRawValue();

    Object.keys(formValues).forEach(k => {
      if (k.startsWith('line_')) {
        const line = formValues[k];
        if (line) {
          this.totalAmountArray.push(Number(line?.totalAmount || 0));
          this.totalAdditionalWorkingDaysArray.push(Number(line?.additionalWorkingDays || 0));
        }
      }
    });

    this.totalAmount = this.totalAmountArray.reduce((sum, val) => sum + val, 0);
    this.totalNewWorkingDays = this.totalAdditionalWorkingDaysArray.reduce((sum, val) => sum + val, 0);

    this.cdRef.detectChanges();
  }

  unlinkRequest(cor: any) {
    if (!this.editable || !this.data?.id) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        `Remove request #${cor.request_no} from this change order?`,
        'Its lines will be removed.',
        'Remove',
        'Cancel',
      ),
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(UnlinkRequestFromChangeOrderRequest({
          payload: {
            changeOrderId: this.data.id,
            corId: cor.uuid,
            requestNo: cor.request_no,
            projectId: this.project?.id,
          },
        }));
      }
    });
  }

  isSourcedLine(lineKey: string): boolean {
    return !!this.lineSources[lineKey];
  }

  deletePicture(pic) {
    if (pic.fileType === 'picture' || pic.fileType === 'document') {
      let pictures = this.getPictures();

      pictures = pictures?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getVideos();

      videos = videos?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('videos')?.setValue(JSON.stringify(videos));
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }
  }

  getPictures() {
    const pictures = this.form.controls['pictures']?.value || '[]';
    return JSON.parse(pictures);
  }

  getVideos() {
    const videos = this.form.controls['videos']?.value || '[]';
    return JSON.parse(videos);
  }

  save() {
    this.displayError = false;
    this.displayLineError = false;
    this.displayTotalAmountError = false;
    this.displayTotalNewWorkingDaysError = false;

    if (this.form.invalid) {
      this.projectsService.checkForErrors.next(true);
      this.validateLineItems();
      this.checkForErrors();
      return;
    }

    this.selectedLineId = null;

    const formData = this.form.getRawValue();

    const lineEntries = Object.entries(formData).filter(([key, _]) =>
      key.startsWith('line_'),
    );

    if (!lineEntries.length && this.totalNewWorkingDays === 0) {
      this.displayError = true;
      this.displayLineError = true;
      setTimeout(() => {
        this.displayErrorElement?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }

    // Validate CO totals don't exceed sum of all linked COR totals
    if (this.linkedRequests?.length) {
      this.corTotalAmount = this.linkedRequests.reduce((sum, cor) => sum + (parseFloat(cor.total_amount) || 0), 0);
      this.corTotalDays = this.linkedRequests.reduce((sum, cor) => sum + (parseFloat(cor.total_additional_days) || 0), 0);

      if (this.corTotalAmount < this.totalAmount) {
        this.displayError = true;
        this.displayTotalAmountError = true;
        setTimeout(() => {
          this.displayErrorElement?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
        return;
      }

      if (this.corTotalDays > 0 && this.corTotalDays < this.totalNewWorkingDays) {
        this.displayError = true;
        this.displayTotalNewWorkingDaysError = true;
        setTimeout(() => {
          this.displayErrorElement?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
        return;
      }
    }

    let payload: any = {
      id: this.data?.id ?? null,
      order_number: formData?.changeOrderNo,
      project_id: this.project?.id,
      sub_contractor_id: formData?.subContractor,
      comments: formData?.descriptionOfChange,
      location: formData?.location,
      pictures: JSON.parse(formData?.pictures),
      videos: JSON.parse(formData?.videos),
    };

    payload = this.manageMedia(payload);

    const lines = [];

    Object.entries(formData)
      .filter(([lineKey, _]: any) => lineKey.startsWith('line_'))
      .forEach(([_, lineKeyValue]: any) => {
        const matchedItem = this.bidItemsList.find(
          o => String(o.item).trim() === String(lineKeyValue?.itemNo).trim(),
        );
        lines.push({
          item: lineKeyValue?.itemNo,
          description: lineKeyValue?.desc,
          quantity: lineKeyValue?.changeInQuantity,
          unit_price: lineKeyValue?.unitPrice,
          uom: lineKeyValue?.uom,
          bid_item_id: matchedItem?.id || null,
          additional_working_days: lineKeyValue?.additionalWorkingDays,
          basis_of_pay: lineKeyValue?.basisOfPay,
          id: lineKeyValue?.id || null,
        });
      });

    payload.lines = lines;

    const temp = {
      type: 'save',
      payload,
    };

    this.store.dispatch(SaveChangeOrdersRequestRequest({ payload: temp }));

    if (this.data?.id) {
      this.analyticsService.capture('change_order_action', {
        action: 'cor_updated',
        project_id: this.project?.id,
      });
    } else {
      this.analyticsService.capture('change_order_action', {
        action: 'cor_created',
        project_id: this.project?.id,
      });
    }
  }

  submit() {
    this.displayError = false;

    // For non-editable statuses, skip validation and go straight to report
    if (this.data?.status === 'submitted' || this.data?.status === 'approved') {
      this.router.navigate([], {
        queryParams: {
          snSubTab: 'workflow',
        },
        queryParamsHandling: 'merge',
      }).then(() => {
        this.dialog.open(ChangeOrdersRequestReportComponent, {
          width: '1000px',
          height: '1100px',
          data: {
            ...this.data,
          },
        });
      });
      return;
    }

    if (this.form.invalid) {
      this.projectsService.checkForErrors.next(true);
      this.validateLineItems();
      this.checkForErrors();
      return;
    }

    this.selectedLineId = null;

    const formData = this.form.getRawValue();

    const lineEntries = Object.entries(formData).filter(([key, _]) =>
      key.startsWith('line_'),
    );

    if (!lineEntries.length && this.totalNewWorkingDays === 0) {
      this.displayError = true;
      this.displayLineError = true;
      setTimeout(() => {
        this.displayErrorElement?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }

    // Validate CO totals don't exceed sum of all linked COR totals
    if (this.linkedRequests?.length) {
      this.corTotalAmount = this.linkedRequests.reduce((sum, cor) => sum + (parseFloat(cor.total_amount) || 0), 0);
      this.corTotalDays = this.linkedRequests.reduce((sum, cor) => sum + (parseFloat(cor.total_additional_days) || 0), 0);

      if (this.corTotalAmount < this.totalAmount) {
        this.displayError = true;
        this.displayTotalAmountError = true;
        setTimeout(() => {
          this.displayErrorElement?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
        return;
      }

      if (this.corTotalDays > 0 && this.corTotalDays < this.totalNewWorkingDays) {
        this.displayError = true;
        this.displayTotalNewWorkingDaysError = true;
        setTimeout(() => {
          this.displayErrorElement?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
        return;
      }
    }

    let payload: any = {
      id: this.data?.id ?? null,
      order_number: formData?.changeOrderNo,
      project_id: this.project?.id,
      sub_contractor_id: formData?.subContractor,
      comments: formData?.descriptionOfChange,
      location: formData?.location,
      pictures: JSON.parse(formData?.pictures),
      videos: JSON.parse(formData?.videos),
    };

    payload = this.manageMedia(payload);

    const lines = [];

    Object.entries(formData)
      .filter(([lineKey, _]: any) => lineKey.startsWith('line_'))
      .forEach(([_, lineKeyValue]: any) => {
        const matchedItem = this.bidItemsList.find(
          o => String(o.item).trim() === String(lineKeyValue?.itemNo).trim(),
        );
        lines.push({
          item: lineKeyValue?.itemNo,
          description: lineKeyValue?.desc,
          quantity: lineKeyValue?.changeInQuantity,
          unit_price: lineKeyValue?.unitPrice,
          uom: lineKeyValue?.uom,
          bid_item_id: matchedItem?.id || null,
          additional_working_days: lineKeyValue?.additionalWorkingDays,
          basis_of_pay: lineKeyValue?.basisOfPay,
          id: lineKeyValue?.id || null,
        });
      });

    payload.lines = lines;

    const temp = {
      type: 'submit',
      payload,
    };

    this.analyticsService.capture('change_order_action', {
      action: 'cor_submitted',
      project_id: this.project?.id,
    });

    this.store.dispatch(SaveChangeOrdersRequestRequest({ payload: temp }));
  }

  manageMedia(data) {
    data.pictures = data.pictures?.map(o => o.id);
    data.videos = data.videos?.map(o => o.id);
    return data;
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;  // Get selected files
    const imageFormData = new FormData();
    const videoFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);

      if (classifyFile(file) === 'video') {
        videoFiles.push(file);
      } else {
        imageFormData.append('inputFiles', file);
      }
    }

    if (imageFormData.has('inputFiles')) {
      this.store.dispatch(UploadFileRequest({ payload: imageFormData }));
    }

    if (videoFiles.length > 0) {
      videoFiles.forEach((videoFile: File) => {
        const videoElement = document.createElement('video');
        const reader = new FileReader();

        reader.onloadend = () => {
          videoElement.src = reader.result as string;

          videoElement.onloadedmetadata = () => {
            const videoDuration = videoElement.duration;
            const maxDuration = this.limits.video_size_in_secs;

            if (videoDuration > maxDuration) {
              this.commonService.notification(
                `Video duration is ${videoDuration} seconds, should be less than ${maxDuration} seconds.`,
                'success',
              );
            } else {
              this.store.dispatch(GetVideoIdRequest({ payload: videoFile }));
            }
          };
        };

        reader.readAsDataURL(videoFile);
      });
    }

    this.fileInputRef.nativeElement.value = '';
  }

  validateLineItems() {
    this.lineErrors = {};
    const invalidLineKeys: string[] = [];

    this.lineKeys.forEach(lineKey => {
      const lineGroup = this.form.get(lineKey) as FormGroup;
      let hasError = false;

      Object.entries(lineGroup.controls).forEach(([controlName, control]) => {
        if (['searchText', 'quantity', 'originalUnitPrice', 'locationOfChange'].includes(controlName)) {
          return;
        }

        if (!control.value || control.value === '') {
          hasError = true;
        }
      });

      this.lineErrors[lineKey] = hasError;

      if (hasError) {
        invalidLineKeys.push(lineKey);
      }
    });

    if (invalidLineKeys.length > 0) {
      this.selectedLineId = invalidLineKeys[0];
      this.cdRef.detectChanges();

      setTimeout(() => {
        const el = document.getElementById(this.selectedLineId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
    }
  }

  getLineGroup(lineKey: string): FormGroup {
    return this.form.get(lineKey) as FormGroup;
  }

  isEllipsisActive(element: HTMLElement): boolean {
    return element.offsetWidth < element.scrollWidth;
  }

  openAddRequestDialog() {
    this.projectsService.openRightPanel.next({
      component: CreateChangeOrderDialogComponent,
      data: {
        projectId: this.project?.id,
        changeOrderId: this.data?.id,
        linkMode: true,
      },
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
